/**
 * Path bank loader/decoder for the Pixi client.
 *
 * Loads data/bank-{rows}.json (the SAME file the server's bank-index.json was
 * built from), asserts the version the server reports, and decodes the path
 * the server chose. path_id semantics match class-plinko-engine.php:
 *   bucket paths: 0 .. paths[bucket].length-1
 *   miss paths:   0 .. miss.length*2-1   (id >= miss.length → mirrored copy)
 *
 * A bank/version mismatch is a hard error, never a silent fallback.
 */
export class PathBank {
  constructor(baseUrl) { this.base = baseUrl; this.banks = new Map(); this.cache = new Map(); }

  async load(rows, expectedVersion) {
    if (this.banks.has(rows)) return this.banks.get(rows);
    const res = await fetch(`${this.base}bank-${rows}.json?ver=${expectedVersion}`, { cache: 'force-cache' });
    if (!res.ok) throw new Error(`bank-${rows}.json: HTTP ${res.status}`);
    const bank = await res.json();
    if (bank.version !== expectedVersion) throw new Error(`bank-${rows} is v${bank.version}, server expects v${expectedVersion}`);
    if (bank.rows !== rows || bank.paths.length !== rows + 1) throw new Error(`bank-${rows} malformed`);
    this.banks.set(rows, bank);
    return bank;
  }

  /** @returns {{xy: Float32Array, frames: number, ripples: Int16Array, mirrored: boolean}} */
  path(rows, bucket, pathId) {
    const key = `${rows}:${bucket}:${pathId}`;
    if (this.cache.has(key)) return this.cache.get(key);
    const bank = this.banks.get(rows);
    if (!bank) throw new Error(`bank-${rows} not loaded`);
    let entry, mirrored = false;
    if (bucket === 'miss') {
      const n = bank.miss.length;
      if (pathId < 0 || pathId >= n * 2) throw new Error(`miss path ${pathId} out of range (0..${n * 2 - 1})`);
      mirrored = pathId >= n;
      entry = bank.miss[pathId % n];
    } else {
      const shelf = bank.paths[bucket];
      if (!shelf || pathId < 0 || pathId >= shelf.length) throw new Error(`path ${pathId} out of range for bucket ${bucket} (0..${shelf ? shelf.length - 1 : '?'})`);
      entry = shelf[pathId];
    }
    const pts = int16(entry.p), q = bank.quant, cx = bank.board.width / 2;
    const frames = pts.length / 2;
    const xy = new Float32Array(pts.length);
    for (let i = 0; i < frames; i++) {
      const px = pts[i * 2] / q, py = pts[i * 2 + 1] / q;
      xy[i * 2] = mirrored ? 2 * cx - px : px;
      xy[i * 2 + 1] = py;
    }
    // ripples: [frame, pegIndex, ...]. A mirrored path touches the mirrored peg.
    let ripples = int16(entry.r);
    if (mirrored) {
      ripples = Int16Array.from(ripples);
      for (let i = 1; i < ripples.length; i += 2) ripples[i] = mirrorPeg(rows, ripples[i]);
    }
    const out = { xy, frames, ripples, mirrored };
    this.cache.set(key, out);
    return out;
  }
}

/** Peg index → index of the peg in the same row, mirrored about the centre. Row r has r+3 pegs. */
function mirrorPeg(rows, idx) {
  let row = 0, start = 0;
  while (row < rows && idx >= start + row + 3) { start += row + 3; row++; }
  const n = row + 3, c = idx - start;
  return start + (n - 1 - c);
}

function int16(b64) {
  const bin = atob(b64), bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return new Int16Array(bytes.buffer, 0, bytes.length >> 1);
}
